import { useId, useMemo } from "react";
import useEvent from "react-use-event-hook";

import { useMapLibreZoom } from "@ryde/controls";
import { useMapColors } from "@ryde/redux";
import {
  CourseRouteTypeEnum,
  DEFAULT_ROUTE_COLOR_OBJECT,
  getContrastingTrailPointColor,
  getCourseCodeMarkerLabel,
  getLatLngFromRenderState,
  getMarkerIconClassName,
  getTrackingMarkerBorderColor,
  hasCourseClientCodeForTrackingMarker,
  IRideRoute,
  MarkerIconType,
  resolveTrackingCourseCode,
  resolveTrackingRenderState,
  serializeIcon,
  TLatLngTuple,
  useDebugMode,
  useDrawers,
  useHaveCustomPermissions,
  useSystemParams,
} from "@ryde/shared";

import { IUseMapLibreTrackingRouteResult } from "./maplibre-tracking-route.models";
import {
  buildTrailSegments,
  resolveTrailMarkerIndices,
} from "./maplibre-tracking-route.utils";

/**
 * Everything a tracked ride needs derived before it can be drawn: its two colours (the trail is drawn in the
 * highlighted variant, the planned line in the plain one), the packed render state, the trail's merged
 * segments, which points get a dot at this zoom, and the course-code pin markup when the ride uses one.
 *
 * Kept out of the component because most of it is expensive and the component re-renders on every zoom change
 * and every tracking poll.
 */
export const useMapLibreTrackingRoute = ({
  route,
  showCarPositionOnly,
  showTrackingTrail,
  dotsMinZoom,
}: {
  route: IRideRoute;
  showCarPositionOnly: boolean;
  showTrackingTrail: boolean;
  dotsMinZoom?: number;
}): IUseMapLibreTrackingRouteResult => {
  const { carTrackingInfo, rideId, courseId, courseLineColor } = route;
  const { debugMode } = useDebugMode();
  const { getMapColorByRideId, getRouteColorIdByRideId } = useMapColors();
  const { openRideEditFormDrawer } = useDrawers();
  const { hasPermissionEditRide, hasPermissionViewRide } =
    useHaveCustomPermissions();
  const { isPassengerCounterButtonShown } = useSystemParams();
  const zoom = useMapLibreZoom();
  const instanceId = useId().replace(/:/g, "");

  const isTrailShown = !showCarPositionOnly && showTrackingTrail;
  const inDetour = carTrackingInfo?.metadata?.inDetour === true;

  const canOpenRideDrawer = Boolean(
    (hasPermissionEditRide || hasPermissionViewRide) && rideId,
  );
  const handleOpenRide = useEvent(() => {
    void openRideEditFormDrawer({ id: rideId });
  });

  const isShuttleManualCountActive =
    Boolean(isPassengerCounterButtonShown) &&
    carTrackingInfo?.metadata?.courseType === CourseRouteTypeEnum.Shuttle;

  // The trail is drawn in the highlighted colour variant, the planned route in the plain one, so the two
  // lines stay distinguishable where they run side by side.
  const colorValue = useMemo(
    () =>
      rideId
        ? courseLineColor?.trim() || getMapColorByRideId(rideId, true)
        : DEFAULT_ROUTE_COLOR_OBJECT.colorValue,
    [courseLineColor, getMapColorByRideId, rideId],
  );
  const plannedLineColor = useMemo(
    () =>
      rideId
        ? courseLineColor?.trim() || getMapColorByRideId(rideId)
        : DEFAULT_ROUTE_COLOR_OBJECT.colorValue,
    [courseLineColor, getMapColorByRideId, rideId],
  );
  const colorId = useMemo(
    () =>
      rideId
        ? getRouteColorIdByRideId(rideId, true)
        : DEFAULT_ROUTE_COLOR_OBJECT.colorId,
    [getRouteColorIdByRideId, rideId],
  );

  const renderState = useMemo(() => resolveTrackingRenderState(route), [route]);

  const carPosition = useMemo<TLatLngTuple | undefined>(() => {
    if (!renderState?.pointCount) {
      return undefined;
    }

    // `getLatLngFromRenderState` returns Leaflet's `TLatLngTuple`, whose third element (altitude) is optional.
    const [lat, lng] = getLatLngFromRenderState(
      renderState,
      renderState.pointCount - 1,
    );

    return [lat, lng];
  }, [renderState]);

  const markerIndices = useMemo(() => {
    if (!renderState || !isTrailShown) {
      return [];
    }

    return resolveTrailMarkerIndices({
      state: renderState,
      zoom,
      minZoom: dotsMinZoom,
    });
  }, [renderState, isTrailShown, zoom, dotsMinZoom]);

  const trailSegments = useMemo(() => {
    if (!renderState?.pointCount || !isTrailShown) {
      return [];
    }

    return buildTrailSegments({
      state: renderState,
      routeColor: plannedLineColor,
    });
  }, [renderState, isTrailShown, plannedLineColor]);

  const coursePin = useMemo(() => {
    const pinArgs = {
      courseId: carTrackingInfo?.courseId,
      courseCode: carTrackingInfo?.courseCode,
      rideId,
      debugMode,
    };

    if (!hasCourseClientCodeForTrackingMarker(pinArgs)) {
      return null;
    }

    const courseCode = resolveTrackingCourseCode(pinArgs);
    const text = courseCode?.trim()
      ? getCourseCodeMarkerLabel(courseCode)
      : undefined;
    const markerFillColor = getTrackingMarkerBorderColor({
      courseLineColor,
      lineColor: colorValue,
    });

    return {
      html: serializeIcon({
        type: MarkerIconType.Point,
        text,
        color: markerFillColor,
        outlineColor: markerFillColor,
        variant: "courseCode",
        inDetour,
      }),
      className: getMarkerIconClassName({
        type: MarkerIconType.Point,
        colorId,
        variant: "courseCode",
        text,
        inDetour,
      }),
    };
  }, [
    carTrackingInfo?.courseCode,
    carTrackingInfo?.courseId,
    colorId,
    colorValue,
    courseLineColor,
    debugMode,
    inDetour,
    rideId,
  ]);

  return {
    renderState,
    carTrackingInfo,
    carPosition,
    // Stable per ride, NOT per poll: the id used to end in the last GPS point's lat/lng, so every tracking
    // refresh minted new source/layer ids and any missed removal left the old segments painted for good.
    trailId: `tracking-trail-${rideId ?? courseId ?? instanceId}`,
    trailSegments,
    markerIndices,
    trailPointColor: getContrastingTrailPointColor(colorValue),
    colorValue,
    inDetour,
    coursePin,
    resolvedPassengersCount: isShuttleManualCountActive
      ? carTrackingInfo?.metadata?.seatsCountByDriver
      : carTrackingInfo?.passengersCount,
    debugMode,
    courseId,
    handleOpenRide: canOpenRideDrawer ? handleOpenRide : undefined,
  };
};
